// src/router/index.ts
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { createApp } from "vue";
import LocationRequest from "@/components/LocationRequest.vue";
import {
  requestAndSendCoordinates,
  clearGeoSyncCache,
  getGeoPermissionState,
  isGeoAllowed,
} from "@/api/coordinates";
import { getMyProfile } from "@/api/user";
import { useUserStore } from "@/stores/user";
import { useRegisterStore } from "@/stores/register";
import { extractReferralCode, savePendingReferralCode } from "@/utils/referrals";

const routes: RouteRecordRaw[] = [
  { path: "/", name: "Welcome", component: () => import("@/pages/Auth.vue") },
  { path: "/login", name: "LoginPhone", component: () => import("@/pages/LoginPhone.vue") },
  { path: "/email", name: "EmailUser", component: () => import("@/pages/EmailUser.vue") },
  { path: "/telegram/phone", name: "TelegramPhone", component: () => import("@/pages/TelegramPhone.vue") },
  { path: "/register", name: "Register", component: () => import("@/pages/register/Welcome.vue") },
  { path: "/register/social-links", name: "RegisterSocialLinks", component: () => import("@/pages/register/SocialLinks.vue") },
  { path: "/register/phone", name: "RegisterSocialPhone", component: () => import("@/pages/register/SocialPhone.vue") },
  { path: "/verify", name: "VerifyCode", component: () => import("@/pages/VerifyCode.vue") },
  { path: "/register/data-user", name: "RegisterDataUser", component: () => import("@/pages/register/DataUser.vue") },
  { path: "/register/success", name: "SuccessRegister", component: () => import("@/pages/register/Success.vue") },
  { path: "/invite/:code", name: "ReferralInvite", component: () => import("@/pages/InvitePage.vue"), meta: { public: true } },
  { path: "/oauth/:type/:token", name: "OAuthCallback", component: () => import("@/pages/OAuthCallback.vue") },
  {
    path: "/oauth/registration-required/:provider",
    name: "OAuthRegistrationRequired",
    component: () => import("@/pages/OAuthRegistrationRequired.vue"),
    meta: { public: true },
  },
  {
    path: "/oauth",
    name: "OAuthErrorCallback",
    component: () => import("@/pages/OAuthRegistrationRequired.vue"),
    meta: { public: true },
  },
  {
    path: "/oauth/need-link/:provider/:link_token",
    name: "OAuthNeedLink",
    component: () => import("@/pages/OAuthNeedLink.vue"),
  },
  { path: "/foundation/search", name: "Search", component: () => import("@/pages/foundation/Search.vue") },
  { path: "/foundation/searchOld", name: "SearchOld", component: () => import("@/pages/foundation/SearchOld.vue") },
  { path: "/foundation/filters", name: "Filters", component: () => import("@/pages/foundation/Filter2.vue") },
  { path: "/under-nav/messenger", name: "Messenger", component: () => import("@/pages/under-nav/Messenger.vue") },
  { path: "/under-nav/activity", name: "Activity", component: () => import("@/pages/under-nav/Activity.vue") },
  { path: "/under-nav/messenger/:id", name: "ChatDetail", component: () => import("@/pages/chat/ChatDetail.vue") },
  { path: "/compatibility", name: "Coincidence", component: () => import("@/components/Coincidence.vue") },
  { path: "/clubs/:id", name: "ClubChat", component: () => import("@/pages/clubs/ClubChatPage.vue") },
  { path: "/likes", name: "Likes", component: () => import("@/pages/under-nav/Likes/LikesLayout.vue"), props: { defaultTab: "likes" } },
  { path: "/top-profiles", name: "TopProfiles", component: () => import("@/pages/under-nav/Likes/LikesTop.vue"), props: { defaultTab: "top" } },
  { path: "/profile", name: "Profile", component: () => import("@/pages/under-nav/Profile/Profile.vue") },
  { path: "/profile/:id", name: "ProfileCard", component: () => import("@/components/card/UserCard.vue") },
  { path: "/reels", name: "Reels", component: () => import("@/pages/reels/Reels.vue") },
  {
    path: "/subscription/gold",
    name: "GoldSubscription",
    component: () => import("@/components/popups/subscription/Gold.vue"),
  },
  {
    path: "/public-profile/:id",
    name: "PublicProfile",
    component: () => import("@/components/profile/profile-friend/ProfileFriend.vue"),
  },
  {
    path: "/settings",
    name: "Settings",
    component: () => import("@/pages/settings/SettingsPage.vue"),
  },
  {
    path: "/settings/referrals",
    name: "Referrals",
    component: () => import("@/pages/settings/ReferralsPage.vue"),
  },
  {
    path: "/settings/location/manual",
    name: "ManualLocation",
    component: () => import("@/pages/settings/ManualLocationPage.vue"),
  },
  {
    path: "/settings/blocked-contacts",
    component: () => import("@/pages/settings/BlockedContactsPage.vue")
  },
  {
    path: "/settings/email-notifications",
    component: () => import("@/pages/settings/EmailNotificationsPage.vue")
  },
  {
    path: "/settings/connected-accounts",
    name: "ConnectedAccounts",
    component: () => import("@/pages/settings/ConnectedAccounts.vue"),
  },
    {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/components/popups/error/NotFound.vue"),
    meta: { public: true },
  },

];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

let GEO_BOOTSTRAP_DONE = false;
let GEO_BOOTSTRAP_TOKEN: string | null = null;

function clearRegistrationAndAuth() {
  const regStore = useRegisterStore();
  regStore.$reset?.();

  const userStore = useUserStore();
  userStore.$reset?.();

  Object.keys(localStorage).forEach((k) => {
    if (k.startsWith("register_")) localStorage.removeItem(k);
  });

  localStorage.removeItem("access_token");
  localStorage.removeItem("device_token");
  localStorage.removeItem("user");
  localStorage.removeItem("social_registration_pending");
}

function isRegistrationInProgress() {
  const stage = localStorage.getItem("register_stage");
  return stage && stage !== "finished";
}

function registrationResumePath() {
  const stage = localStorage.getItem("register_stage");
  if (stage === "email") return "/email";
  if (stage === "phone") return "/register/phone";
  if (stage === "social-links") return "/register/social-links";
  return "/register/data-user";
}

function isTelegramMiniApp() {
  return (window as any).launchEnv === "telegram";
}

function isVkMiniApp() {
  return (window as any).isVKLaunch === true && !isTelegramMiniApp();
}

let TELEGRAM_AUTH_CHECKED = false;
let VK_AUTH_CHECKED = false;

export function markTelegramAuthChecked(checked = true) {
  TELEGRAM_AUTH_CHECKED = checked;
}

export function markVkAuthChecked(checked = true) {
  VK_AUTH_CHECKED = checked;
}

router.beforeEach(async (to, from, next) => {
  console.log("🛰 beforeEach:", from.fullPath, "→", to.fullPath);

  const telegramLaunch = isTelegramMiniApp();
  const vkLaunch = isVkMiniApp();
  const entryReferralCode = to.name === "ReferralInvite"
    ? String(to.params.code || "").trim()
    : extractReferralCode(to.query as Record<string, unknown>);
  const existingToken = localStorage.getItem("access_token");
  const existingRegistrationStage = localStorage.getItem("register_stage");
  const canSaveEntryReferral = !existingToken || (
    existingRegistrationStage !== null && existingRegistrationStage !== "finished"
  );

  if (entryReferralCode && canSaveEntryReferral) {
    savePendingReferralCode(entryReferralCode);
  }

  // Каждый новый запуск Telegram Mini App обязан сначала пройти через Auth.vue
  // и получить актуальный результат /auth/telegram/v2. Сохранённого токена
  // недостаточно для выбора между login и незавершённой регистрацией.
  if (telegramLaunch && !TELEGRAM_AUTH_CHECKED) {
    if (from.matched.length === 0) {
      localStorage.removeItem("registration_aborted");
    }

    if (to.path !== "/") {
      console.log("🔐 Telegram: сначала обязательная проверка /auth/telegram/v2");
      return next("/");
    }
  }


  // VK Mini Apps always validates the signed launch parameters before any
  // route (including a deep link) can render. The root route contains only
  // the Mini App loading/error state while this check is running.
  if (vkLaunch && !VK_AUTH_CHECKED && to.path !== "/") {
    return next({ path: "/", query: to.query, hash: to.hash });
  }

  /* ===========================================================
     🔥 0. Блокируем регистрацию, если пользователь её отменил
     =========================================================== */
  if (localStorage.getItem("registration_aborted") === "1") {
    if (to.path.startsWith("/register") || to.path === "/email") {
      console.log("⛔ Регистрация отменена — блокируем вход в /register и /email");
      return next("/");
    }
  }

  const token = localStorage.getItem("access_token");
  const userStore = useUserStore();
  const regStore = useRegisterStore();

  /* ===========================================================
     1️⃣ Попытка зайти в /register/... БЕЗ токена → на /
     =========================================================== */
  if (!token && to.path.startsWith("/register") && to.path !== "/register") {
    console.warn("⛔ Переход в регистрацию без токена — сбрасываем данные и отправляем на /");
    clearRegistrationAndAuth();
    return next("/");
  }

  const isRegisterFlow = to.path.startsWith("/register");
  const registrationTarget = registrationResumePath();

  /* ===========================================================
     2️⃣ Токен есть, но регистрация НЕ завершена → возвращаем в onboarding
     =========================================================== */
  if (
    token &&
    isRegistrationInProgress() &&
    !isRegisterFlow &&
    to.path !== registrationTarget &&
    !(telegramLaunch && !TELEGRAM_AUTH_CHECKED) &&
    !(vkLaunch && !VK_AUTH_CHECKED)
  ) {
    console.warn("❗ Незавершённая регистрация → возвращаем на обязательный шаг");
    return next(registrationTarget);
  }

  /* ===========================================================
     3️⃣ TELEGRAM: без токена разрешаем только auth + register
     =========================================================== */
  if (telegramLaunch) {
    const allowedPages = ["/", "/email", "/login", "/verify", "/telegram/phone"];
    const isSocialRegistrationWarning = to.path.startsWith("/oauth/registration-required/");
    if (!token && !allowedPages.includes(to.path) && !isSocialRegistrationWarning) {
      console.log("⛔ Telegram: без токена нельзя на", to.path);
      return next("/");
    }

  }


  if (vkLaunch && !token && to.path !== "/") {
    return next("/");
  }

  /* ===========================================================
     4️⃣ Публичные маршруты
     =========================================================== */
  const publicPages: (string | RegExp)[] = [
    "/",
    "/login",
    "/verify",
    "/email",
    "/telegram/phone",
    "/register",
    /^\/oauth\/[^\/]+\/[^\/]+$/,
    /^\/oauth\/need-link\/[^\/]+\/[^\/]+$/,
    /^\/oauth\/registration-required\/[^\/]+$/,
    /^\/public-profile\/[^\/]+$/,
  ];

   const isPublicPath = (path: string): boolean =>
    publicPages.some((item) =>
      typeof item === "string" ? item === path : item.test(path)
    );

  const authRequired = !(isPublicPath(to.path) || to.matched.some((record) => record.meta?.public));

  /* ===========================================================
     5️⃣ Прелоад поиска (оставляем как есть)
     =========================================================== */
  if (token && (to.path === "/" || to.path === "/foundation/search")) {
    import("@/preload/SearchPreloader").then(({ preloadSearchChunk }) => {
      try {
        preloadSearchChunk();
      } catch (e) {
        console.warn("preloadSearchChunk error:", e);
      }
    });
  }

  /* ===========================================================
     6️⃣ Если маршрут публичный
     =========================================================== */
  if (!authRequired) {
    if (token && to.name === "PublicProfile" && !userStore.user) {
      try {
        await userStore.fetchUser();
      } catch (err) {
        console.warn("Ошибка загрузки сессии для публичного профиля:", err);
      }
    }

    // переход на "/" с токеном
    if (to.path === "/" && token) {
      if (telegramLaunch && !TELEGRAM_AUTH_CHECKED) {
        return next();
      }
      if (vkLaunch && !VK_AUTH_CHECKED) {
        return next();
      }
      const storedUser =
        userStore.user || JSON.parse(localStorage.getItem("user") || "null");

      // info — это information, а фотки лежат в storedUser.photos
      function incomplete(info: any, photos: any[] | undefined): boolean {
        if (!info) return true;
        if (!info.name) return true;
        if (!info.birth_date) return true;
        if (!info.gender) return true;
        if (!info.interests || info.interests.length < 3) return true;
        // тут уже проверяем фотки корректно
        if (!photos || photos.length < 2) return true;
        return false;
      }

      // ⚠️ ВАЖНО: не трогаем токен, если вообще нет закешированного профиля
      if (storedUser && incomplete(storedUser.information, storedUser.photos)) {
        console.warn("🚫 Профиль не завершён → очищаем токен и возвращаем на Auth");
        clearRegistrationAndAuth();
        return next("/");
      }

      return next("/foundation/search");
    }

    return next();
  }

  /* ===========================================================
     7️⃣ Закрытый маршрут, но токена НЕТ → на /
     =========================================================== */
  if (authRequired && !token) {
    console.warn("🚫 Нет токена — редирект на /");
    return next("/");
  }

  /* ===========================================================
     8️⃣ Закрытый маршрут + есть токен
     =========================================================== */

  // 🔥🔥🔥 ГЕОЛОКАЦИЯ — ТОЛЬКО ОДИН РАЗ ЗА СЕССИЮ 🔥🔥🔥
  if (GEO_BOOTSTRAP_TOKEN !== token) {
    if (GEO_BOOTSTRAP_TOKEN !== null) {
      clearGeoSyncCache();
    }
    GEO_BOOTSTRAP_DONE = false;
    GEO_BOOTSTRAP_TOKEN = token;
  }

  if (!to.path.startsWith("/register") && !GEO_BOOTSTRAP_DONE) {
    try {
      await ensureGeoBootstrap();
      GEO_BOOTSTRAP_DONE = true; // ← ВАЖНО
    } catch (e) {
      console.warn("geo bootstrap error:", e);
    }
  }

  if (!userStore.user) {
    getMyProfile()
      .then((profile) => {
        if (profile?.information) userStore.setUser(profile);
      })
      .catch((err) => console.warn("Ошибка загрузки профиля:", err));
  }

  return next();
});

function isDesktopChrome(): boolean {
  return (
    /Chrome/.test(navigator.userAgent) &&
    !/Android/.test(navigator.userAgent) &&
    !/Mobile/.test(navigator.userAgent)
  );
}

async function tryRealGeoAccess(): Promise<"ok" | "denied" | "error"> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      return resolve("error");
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        resolve("ok");
      },
      (err) => {
        // 1 — PERMISSION_DENIED, 2 — POSITION_UNAVAILABLE, 3 — TIMEOUT
        if (err.code === 1) {
          resolve("denied");
        } else {
          resolve("error");
        }
      },
      {
        timeout: 5000,
        maximumAge: 0,
        enableHighAccuracy: !isDesktopChrome(),
      }
    );
  });
}

// ========== GEO BOOTSTRAP (UNIFIED DESKTOP + MOBILE) ==========
export async function ensureGeoBootstrap(): Promise<void> {
  const token = localStorage.getItem("access_token");
  if (!token) return;

  const permission = await getGeoPermissionState();
  const alreadyAllowed = isGeoAllowed();

  console.log(
    "[geo-bootstrap] permission =",
    permission,
    "geo_allowed =",
    alreadyAllowed
  );

  /* 
    CASE 1 — В нашем приложении ещё НИ РАЗУ не было успешного разрешения гео.
    (geo_allowed=false) — это первый запрос → показываем нашу модалку
    и только из неё дергаем getCurrentPosition.
  */
  if (!alreadyAllowed) {
    console.log("[geo-bootstrap] geo_allowed=false → first request → show modal");
    await showGeoOverlay();
    return;
  }

  /*
    CASE 2 — Пользователь уже разрешал гео раньше (geo_allowed=true).
    Проверяем, не выключил ли он его сейчас в браузере / системе.
  */
  const real = await tryRealGeoAccess();
  console.log("[geo-bootstrap] real access =", real);

  // Если теперь браузер/ОС гео не даёт — снова показываем модалку
  if (permission === "denied" || real === "denied" || real === "error") {
    console.warn("[geo-bootstrap] geo now denied/unavailable → show modal");
    await showGeoOverlay();
    return;
  }

  /*
    CASE 3 — Всё ок: гео уже разрешено и реально работает.
    Тихо обновляем координаты без лишних модалок.
  */
  try {
    await requestAndSendCoordinates(false);
  } catch (e) {
    console.warn("[geo-bootstrap] error refreshing coords", e);
  }
}

function showGeoOverlay(): Promise<void> {
  return new Promise((resolve) => {
    const mountEl = document.createElement("div");
    document.body.appendChild(mountEl);

    const app = createApp(LocationRequest, {
      onSuccess: () => {
        app.unmount();
        mountEl.remove();
        resolve();
      },
      onDenied: () => {
        app.unmount();
        mountEl.remove();
        resolve();
      },
    });

    app.mount(mountEl);
  });
}

// ========== CHUNK ERROR HANDLER ==========
router.onError((error) => {
  const message = (error as any)?.message || "";
  if (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed")
  ) {
    console.warn("Chunk load error → reload");
    window.location.reload();
  } else {
    console.error("Router error:", error);
  }
});

export default router;
